import { Plus, Trash2 } from "lucide-react";
import {
  useFieldArray,
  type Control,
  type UseFormRegister,
} from "react-hook-form";

import type { CauseFormData, CausePartyFormData } from "./causeList.types";

interface CausePartiesSectionProps {
  control: Control<CauseFormData>;
  register: UseFormRegister<CauseFormData>;
}

const emptyParty = (
  type: CausePartyFormData["type"] = "petitioner",
): CausePartyFormData => ({
  name: "",
  type,
});

const CausePartiesSection = ({
  control,
  register,
}: CausePartiesSectionProps) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name: "parties",
  });

  return (
    <div className="space-y-4 lg:col-span-2">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h4 className="text-sm font-semibold text-gray-900">Parties</h4>

          <p className="text-xs text-gray-500">
            Add petitioner and respondent parties.
          </p>
        </div>

        <button
          type="button"
          onClick={() => append(emptyParty("petitioner"))}
          className="inline-flex items-center justify-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-100"
        >
          <Plus className="h-4 w-4" />
          Add Party
        </button>
      </div>

      <div className="space-y-3">
        {fields.map((field, index) => (
          <PartyRow
            key={field.id}
            index={index}
            canRemove={fields.length > 1}
            register={register}
            onRemove={() => remove(index)}
          />
        ))}
      </div>
    </div>
  );
};

interface PartyRowProps {
  index: number;
  canRemove: boolean;
  register: UseFormRegister<CauseFormData>;
  onRemove: () => void;
}

const PartyRow = ({ index, canRemove, register, onRemove }: PartyRowProps) => {
  return (
    <div className="rounded-xl border border-gray-200 bg-gray-50 p-3">
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-[150px_1fr_auto]">
        <div>
          <label className="mb-1 block text-xs font-semibold text-gray-700">
            Type
          </label>

          <select
            {...register(`parties.${index}.type`)}
            className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm outline-0 focus:border-blue-400 focus:ring-1 focus:ring-blue-400"
          >
            <option value="petitioner">Petitioner</option>
            <option value="respondent">Respondent</option>
          </select>
        </div>

        <div>
          <label className="mb-1 block text-xs font-semibold text-gray-700">
            Party Name
          </label>

          <input
            {...register(`parties.${index}.name`)}
            className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm outline-0 focus:border-blue-400 focus:ring-1 focus:ring-blue-400"
            placeholder="Enter party name"
          />
        </div>

        <div className="flex items-end">
          <button
            type="button"
            disabled={!canRemove}
            onClick={onRemove}
            className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-red-100 bg-white text-red-500 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-40"
            title="Remove party"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default CausePartiesSection;
